/**
 * Thin axios wrapper — one place to attach the auth header, base URL and
 * common error handling for future UI redesign work.
 *
 * This module is ADDITIVE. The existing `App.js` continues to use its own
 * `axios` import unchanged. When redesigning a page, you can migrate that
 * page to import `apiClient` from here and get consistent behaviour across
 * the app.
 *
 * The backend contract is unchanged — this file only proxies HTTP calls.
 *
 * Usage:
 *   import { apiClient } from './api/client';
 *   import { endpoints } from './api/endpoints';
 *   const { data } = await apiClient.get(endpoints.components.list);
 */

import axios from 'axios';

const BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Attach the JWT from localStorage on every request. Same key App.js uses
// today (`token`) — kept identical so the two axios instances stay in sync.
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 401 → boot the user out. Redesigned pages will surface a friendlier
// "session expired" toast; today we simply clear the token so the global
// `<AuthProvider>` re-renders the login screen on next paint.
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    return Promise.reject(error);
  }
);

/**
 * Convenience helper for downloading files (PDF/Excel) — sets responseType
 * to blob and returns an object URL callers can hand straight to a
 * hidden <a download>.
 */
export const downloadBlob = async (path, filename) => {
  const response = await apiClient.get(path, { responseType: 'blob' });
  const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = blobUrl;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
};
